import React from "react";
import { Link, useLocation } from "react-router-dom";
import { scrollToHash, splitHashTarget } from "../../utils/scrollToHash";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { HiOutlineMail, HiOutlineLocationMarker, HiOutlinePhone, HiOutlineClock } from "react-icons/hi";
import { SITE_CONFIG } from "../../constants/config";
import { FOOTER_LINKS } from "../../constants/navigation";
import Container from "../ui/Container";
import GradientText from "../ui/GradientText";

export default function Footer() {
    const currentYear = new Date().getFullYear();
    const location = useLocation();
    const whatsappLink = `${SITE_CONFIG.whatsappUrl}?text=${encodeURIComponent(SITE_CONFIG.whatsappMessage)}`;

    /**
     * Renders a footer link.
     *
     * An anchor pointing at the page you are already on is rendered as a
     * native <a href="#section">, because React Router does not re-run
     * location effects for a hash-only change and would update the URL
     * without scrolling. See utils/scrollToHash.
     */
    const FooterLink = ({ link }) => {
        const { path, hash } = splitHashTarget(link.path);
        const className = "text-zinc-400 hover:text-brand-400 transition-colors text-body-sm";

        if (hash && path === location.pathname) {
            return (
                <a href={hash} className={className} onClick={() => scrollToHash(hash)}>
                    {link.label}
                </a>
            );
        }

        return (
            <Link to={link.path} className={className}>
                {link.label}
            </Link>
        );
    };

    // Only confirmed, active company profiles are listed.
    const socialLinks = [
        { icon: <FaFacebookF size={18} />, href: SITE_CONFIG.social.facebook, label: "Facebook" },
        { icon: <FaInstagram size={18} />, href: SITE_CONFIG.social.instagram, label: "Instagram" },
    ];

    // overflow-hidden clips the 600px glow line below, which is wider than a
    // phone screen and would otherwise widen the page.
    return (
        <footer className="relative overflow-hidden bg-surface-card border-t border-white/[0.08]">
            {/* Ambient glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-brand-500/60 to-transparent" />

            <Container>
                {/* Main Footer */}
                <div className="py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
                    {/* Brand Column */}
                    <div className="lg:col-span-2">
                        <Link to="/" className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 rounded-xl overflow-hidden">
                                <img
                                    src="/assets/branding/prodevop-logo-white-transparent-512x512.png"
                                    alt="ProDevop"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <span className="font-display font-bold text-xl text-white">
                                ProDevop
                            </span>
                        </Link>
                        <p className="text-zinc-400 text-body-base mb-6 max-w-sm">
                            Custom digital solutions for startups and growing businesses — websites, e-commerce, POS systems, mobile apps, branding, and digital marketing. Based in Kandy, Sri Lanka, and open to suitable international projects.
                        </p>

                        {/* Contact Info */}
                        <div className="flex flex-col gap-3 mb-6">
                            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-brand-300 transition-colors text-body-sm">
                                <HiOutlinePhone size={18} />
                                {SITE_CONFIG.phoneDisplay}
                            </a>
                            <a href={`mailto:${SITE_CONFIG.email}`} className="flex items-center gap-3 text-zinc-400 hover:text-brand-300 transition-colors text-body-sm break-all">
                                <HiOutlineMail size={18} />
                                {SITE_CONFIG.email}
                            </a>
                            <span className="flex items-center gap-3 text-zinc-400 text-body-sm">
                                <HiOutlineLocationMarker size={18} />
                                {SITE_CONFIG.location}
                            </span>
                            <span className="flex items-center gap-3 text-zinc-400 text-body-sm">
                                <HiOutlineClock size={18} />
                                {SITE_CONFIG.businessHours}
                            </span>
                        </div>

                        {/* Social Links */}
                        <div className="flex items-center gap-3">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.label}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.label}
                                    className="w-10 h-10 rounded-lg bg-white/[0.04] hover:bg-brand-500/20 border border-white/[0.08] hover:border-brand-500/30 hover:shadow-glow-purple-sm flex items-center justify-center text-zinc-400 hover:text-brand-300 transition-all duration-300"
                                >
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Services */}
                    <div>
                        <h4 className="font-display font-semibold text-white mb-5 text-body-base">Services</h4>
                        <ul className="flex flex-col gap-3">
                            {FOOTER_LINKS.services.map((link) => (
                                <li key={link.path}>
                                    <FooterLink link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h4 className="font-display font-semibold text-white mb-5 text-body-base">Company</h4>
                        <ul className="flex flex-col gap-3">
                            {FOOTER_LINKS.company.map((link) => (
                                <li key={link.path}>
                                    <FooterLink link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Resources */}
                    <div>
                        <h4 className="font-display font-semibold text-white mb-5 text-body-base">Resources</h4>
                        <ul className="flex flex-col gap-3">
                            {FOOTER_LINKS.resources.map((link) => (
                                <li key={link.path}>
                                    <FooterLink link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="py-6 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-center md:text-left">
                        <p className="text-zinc-400 text-body-sm">
                            &copy; {currentYear} <GradientText>{SITE_CONFIG.companyName}</GradientText>. All rights reserved.
                        </p>
                        <p className="text-zinc-400 text-body-xs mt-1">
                            Company Registration No. {SITE_CONFIG.registrationNumber}
                        </p>
                    </div>
                    <div className="flex items-center gap-6">
                        {FOOTER_LINKS.legal.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className="text-zinc-400 hover:text-zinc-200 transition-colors text-body-sm"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </Container>
        </footer>
    );
}
